import VariableService from '@/services/variables';
import type { Variable, VariablesResponse } from '@/types/variable';
import { TextVariablePayload } from '@/types/variable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// Query keys
export const variableKeys = {
  all: ['variables'] as const,
  lists: () => [...variableKeys.all, 'list'] as const,
  list: (filters: any) => [...variableKeys.lists(), filters] as const,
  details: () => [...variableKeys.all, 'detail'] as const,
  detail: (id: string) => [...variableKeys.details(), id] as const,
};

// Get all variables
export const useGetVariables = (filters: any, enabled: boolean) => {
  return useQuery<VariablesResponse>({
    queryKey: variableKeys.list(filters),
    enabled,
    queryFn: () =>
      VariableService.getKbresources({
        ...filters,
      }) as Promise<VariablesResponse>,
  });
};

// Get single variable
export const useGetVariableById = (id: string) => {
  return useQuery<Variable>({
    queryKey: variableKeys.detail(id),
    queryFn: () =>
      VariableService.getKbresourcesById(parseInt(id)) as Promise<Variable>,
    enabled: !!id,
  });
};

interface UseCreateOrUpdateVariableOptions {
  onSuccess?: () => void;
  successMessage?: string;
  showToast?: boolean;
}

// Create or update variable
export const useCreateOrUpdateVariable = ({
  onSuccess,
  successMessage,
  showToast = true,
}: UseCreateOrUpdateVariableOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: number;
      data: TextVariablePayload;
    }) => {
      if (id) {
        return VariableService.updateKbresources(id, data);
      }
      return VariableService.createKbresources(data);
    },
    onSuccess: (_, variables) => {
      const message =
        successMessage ??
        (variables.id
          ? 'Variable updated successfully'
          : 'Variable created successfully');
      if (showToast) {
        toast.success(message, { position: 'top-right' });
      }

      queryClient.invalidateQueries({ queryKey: variableKeys.lists() });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: variableKeys.detail(variables.id.toString()),
        });
      }
      onSuccess?.();
    },
    onError: (error) => {
      toast.error('Failed to save variable. Please try again.');
      console.error('Failed to save variable:', error);
    },
  });
};

// Delete variable
export const useDeleteVariable = ({
  onSuccess,
  successMessage,
}: UseCreateOrUpdateVariableOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => VariableService.deleteKbresources(id),
    onSuccess: () => {
      toast.success(successMessage ?? 'Variable deleted successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: variableKeys.lists() });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error('Failed to delete variable. Please try again.');
      console.error('Failed to delete variable:', error);
    },
  });
};
